import { ExportExcelParseOption } from '@/appLogic/interfaces/exportExcel';
import { COLUMN_NAME_FOR_EXPORT_FILE } from '../../constants';
import { IYearMonth } from '@/coreDomainsModule/date';

/**
 * APIから取得する仕入先内示のダウンロードに使用するデータ
 */
export interface ISupplierForecastExportDataResult {
  supplierForecastTemplateExcelData: Blob;
  data: SupplierForecastExportData[];
}

/**
 * 仕入先内示のダウンロードに使用するデータの型
 */
export type SupplierForecastExportData = ISupplierForecastExportData | ISubTotalSupplierForecastExportData;

/**
 * 仕入先内示エクセルオプションデータ
 */
export const defaultExportExcelPageNames: [[string, string], ExportExcelParseOption][] = [
  [
    ['実績&内示', 'A2'],
    {
      separateYmd: {
        startCell: 'K1',
        separateCount: 1,
      },
      fileName: '',
      sheetName: '実績&内示',
      ignoreRow: [],
      valueStartCell: 'K3',
      addColumnName: [
        { colName: '内示', isUpdateCalender: true },
        { colName: '販売実績', isUpdateCalender: true },
      ],
    },
  ],
];

/**
 * 仕入先内示エクセルで、末尾に追加される数量を扱う列の名前
 */
export type AddColumnUseAmountType = 'supplierForecast' | 'salesResult';

/**
 * 仕入先内示エクセルで、末尾に追加される数量を扱う列の情報
 */
export interface IAddColumnUseAmount {
  /**
   * 列のタイプ
   */
  type: AddColumnUseAmountType;
  /**
   * 対象の年月
   */
  yearMonth: IYearMonth;
  /**
   * 数量
   */
  amount: number;
}

/**
 * 仕入先内示のダウンロードに使用する基本的なデータ
 */
export interface ISupplierForecastExportBaseData {
  /**
   * 集計取引ID
   */
  aggregateBusinessId: string;
  /**
   * 仕入先CD
   */
  supplierNumber: string;
  /**
   * 仕入先名
   */
  supplierName: string;
  /**
   * 商品CD
   */
  businessNumber: string;
  /**
   * 商品名
   */
  businessName: string;
}

/**
 * 仕入先内示のダウンロードに使用するデータ
 */
export interface ISupplierForecastExportData extends ISupplierForecastExportBaseData {
  /**
   * 置場CD
   */
  warehouseNumber: string;
  /**
   * 置場名
   */
  warehouse: string;
  /**
   * 容量
   */
  package: number;
  /**
   * メモ1
   */
  memo1?: string;
  /**
   * メモ2
   */
  memo2?: string;
  /**
   * 販売実績の情報
   */
  SalesResult?: SalesResultExportData;
  /**
   * 仕入先内示の詳細情報
   */
  SupplierForecasts: Array<SupplierAnnounceExportData>;
}

/**
 * 販売実績の情報(出力用)
 */
export interface SalesResultExportData {
  /**
   * 販売実績サマリID
   * @type {string}
   * @memberof SalesResultSummaryExportData
   */
  salesResultSummaryId: string;
  /**
   * 販売実績対象年
   * @type {number}
   * @memberof SalesResultSummaryExportData
   */
  salesResultTargetYear: number;
  /**
   * 販売実績対象月
   * @type {number}
   * @memberof SalesResultSummaryExportData
   */
  salesResultTargetMonth: number;
  /**
   * 販売数量
   * @type {number}
   * @memberof SalesResultSummaryExportData
   */
  salesResultAmountTotal: number;
}

/**
 * 仕入先内示の詳細情報(出力用)
 */
export interface SupplierAnnounceExportData {
  /**
   * 仕入先内示ID
   */
  supplierForecastId: string;
  /**
   * 「= 客先内示」にチェックを入れて作成されたかどうか
   */
  equalCustomerForecastDataFlag: boolean;
  /**
   * 仕入先内示作成年
   */
  supplierForecastCreatedYear: number;
  /**
   * 仕入先内示作成月
   */
  supplierForecastCreatedMonth: number;
  /**
   * 仕入先内示対象年
   */
  supplierForecastTargetYear: number;
  /**
   * 仕入先内示対象月
   */
  supplierForecastTargetMonth: number;
  /**
   * 仕入先内示数量
   */
  supplierForecastAmount: number;
}

/**
 * 仕入先内示出力書式の合計行のデータ
 */
export interface ISubTotalSupplierForecastExportData extends ISupplierForecastExportBaseData {
  /**
   * 販売実績の合計情報
   */
  sumSalesResult?: SubTotalAmountInfo;

  /**
   * 仕入先内示の合計情報
   */
  sumSupplierForecasts: SubTotalAmountInfo[];
}

/**
 * 数量の合計情報
 */
export interface SubTotalAmountInfo {
  /**
   * 対象年
   */
  targetYear: number;
  /**
   * 対象月
   */
  targetMonth: number;
  /**
   * 数量
   */
  amountTotal: number;
}

/**
 * 仕入先内示の出力書式の列名
 */
export type ColumnNamesOfExportFile = (typeof COLUMN_NAME_FOR_EXPORT_FILE)[number];

/**
 * 出力書式の列名に対応しているかどうか
 * @param arg - 列名
 */
export const isColumnNamesOfExportFile = (arg: string): arg is ColumnNamesOfExportFile => {
  return COLUMN_NAME_FOR_EXPORT_FILE.some((column) => column === arg);
};

/**
 * 合計行のデータかどうか
 * @param arg - データ
 */
export const isSubTotalSupplierForecastExportData = (arg: any): arg is ISubTotalSupplierForecastExportData => {
  return arg?.sumSalesResults !== undefined || arg?.sumSupplierForecasts !== undefined;
};
